'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export interface MembershipRegistrationData {
  fullName: string;
  email: string;
  phone?: string;
  tierId: string;
  tierName: string;
  tierPrice: string;
  message?: string;
}

export const membershipService = {
  async register(data: MembershipRegistrationData) {
    const supabase = createClient();

    try {
      const { data: result, error } = await supabase
        .from('membership_registrations')
        .insert({
          full_name: data.fullName,
          email: data.email,
          phone: data.phone,
          tier_id: data.tierId,
          tier_name: data.tierName,
          tier_price: data.tierPrice,
          message: data.message,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Membership registration error:', error.message);
          return null;
        }
      }

      return {
        id: result.id,
        fullName: result.full_name,
        email: result.email,
        phone: result.phone,
        tierId: result.tier_id,
        tierName: result.tier_name,
        tierPrice: result.tier_price,
        message: result.message,
        status: result.status,
        registeredAt: result.registered_at
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAll() {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('membership_registrations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else if (error.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return data?.map(row => ({
        id: row.id,
        fullName: row.full_name,
        email: row.email,
        phone: row.phone,
        tierId: row.tier_id,
        tierName: row.tier_name,
        tierPrice: row.tier_price,
        message: row.message,
        status: row.status,
        registeredAt: row.registered_at
      })) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  }
};